import { getSubjectLabels } from '@/lib/settings';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { masterdataApi, type FachItem, type KursItem } from '../../lib/courses';
import { lehrerApi, type LehrerOption, type UnterrichtseinheitDTO } from '../../lib/unterrichtseinheiten';
import { type Room, type RoomUnit } from '../../lib/rooms';
import { type TrialLessonDTO } from '../../lib/trials';
import { useStandort } from '../layouts/use-standort';
import { api, historyTemplatesApi } from '../../lib/api';
import WizardShell from '../ui/wizard-shell';
import { type TeacherAvailabilitySlot } from '../../lib/personen';
import { trialRequestsApi } from '../../lib/trialRequests';
import { WizardPill, WizardPills } from '../ui/wizard-layout';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { formatDate, toApiDate } from '../../lib/dateFormat';
import { type PreviewEvent, type TeacherTimetableSelectEvent } from '../schedule/TeacherTimetable';
import { type RecipientGroup } from '../communications/CommunicationComposer';
import StepDateSelection from './trialWizard/StepDateSelection';
import StepRoomSelection from './trialWizard/StepRoomSelection';
import StepParticipantSelection from './trialWizard/StepParticipantSelection';
import StepCommunication from './trialWizard/StepCommunication';
import StepSummary from './trialWizard/StepSummary';
import StepLessonSchedule from './trialWizard/StepLessonSchedule';
import type {
  SelectedSlot,
  SelectedTeilnehmerState,
  Step1Section,
  TrialParticipantDraft,
  TrialRequestPerson,
} from './trialWizard/types';
import {
  resolveCoursePickFromEventAction,
  submitTrialWizardAction,
} from './trialWizard/actions';
import { useTrialWizardEffects } from './trialWizard/useTrialWizardEffects';
import { geburtsdatumFuerMail } from '../../lib/geburtsdatumFuerMail';
import {
  LEERE_KUNDENDATEN,
  LEERER_WERT,
  fehlendeKundenfelder,
  fehlendeSchuelerfelder,
  gewaehlteProfilId,
  stammdatenApi,
  type KundeUndProfilWert,
  type ProfilZeile,
} from '../../lib/api/stammdaten';

export interface TrialWizardModalProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  defaultFachId?: number;
  defaultTeacherId?: string | number;
  lockTeacher?: boolean; // z.B. Terminplan: Lehrer ist bereits ausgewählt
  requireDateSelection?: boolean; // Bei Termin-Vorgabe: direkt zu Schritt 2 starten
  trialRequestId?: number; // Umwandeln einer Probestunden-Anfrage
  trialRequestPerson?: TrialRequestPerson;
  defaultDate?: string; // YYYY-MM-DD
  defaultStartTime?: string; // HH:MM
  defaultDayOfWeek?: number; // 1-7 (Montag=1)
  defaultRoomId?: number;
  defaultCourseId?: number;
  defaultSubprofileId?: string | number;
  defaultParentPersonId?: string | number;
  onCreated?: (trial: unknown) => void;
}

type Step = 1 | 2 | 3 | 4 | 5 | 6;

const toHHMM = (d: Date): string => {
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
};
const addMinutes = (hhmm: string, minutes: number): string => {
  const [h, m] = hhmm.split(':').map(Number);
  const base = new Date(1970, 0, 1, h || 0, m || 0, 0);
  base.setMinutes(base.getMinutes() + minutes);
  return toHHMM(base);
};
const overlaps = (startA: string, endA: string, startB: string, endB: string): boolean => {
  return startA < endB && startB < endA; // strict overlap on HH:MM strings
};

const toGermanDow = (date: Date): number => {
  const jsDow = date.getDay();
  return jsDow === 0 ? 7 : jsDow;
};

const buildInitialSlotFromDefaults = (params: {
  defaultStartTime?: string;
  defaultDate?: string;
  defaultDayOfWeek?: number;
  requireDateSelection: boolean;
}): { slot: SelectedSlot; date: string; startTime: string; timetableDayOfWeek: number } => {
  const { defaultStartTime, defaultDate, defaultDayOfWeek, requireDateSelection } = params;
  const now = new Date();
  const defaultDow = defaultDayOfWeek ? Number(defaultDayOfWeek) : toGermanDow(now);

  if (!defaultStartTime) {
    return { slot: null, date: defaultDate || '', startTime: '', timetableDayOfWeek: defaultDow };
  }

  if (defaultDate) {
    const parsed = new Date(`${defaultDate}T00:00:00`);
    const parsedDow = Number.isNaN(parsed.getTime()) ? defaultDow : toGermanDow(parsed);
    return {
      slot: { dayOfWeek: parsedDow, startTime: `${defaultStartTime}:00`, endTime: addMinutes(defaultStartTime, 30) },
      date: defaultDate,
      startTime: defaultStartTime,
      timetableDayOfWeek: parsedDow,
    };
  }

  if (!defaultDayOfWeek) {
    return {
      slot: { dayOfWeek: defaultDow, startTime: `${defaultStartTime}:00`, endTime: addMinutes(defaultStartTime, 30) },
      date: '',
      startTime: defaultStartTime,
      timetableDayOfWeek: defaultDow,
    };
  }

  let date = '';
  if (!requireDateSelection) {
    const today = new Date();
    const currentDow = toGermanDow(today);
    let daysToAdd = defaultDayOfWeek - currentDow;
    if (daysToAdd < 0) daysToAdd += 7;
    const targetDate = new Date(today);
    targetDate.setDate(today.getDate() + daysToAdd);
    date = toApiDate(targetDate);
  }

  return {
    slot: { dayOfWeek: defaultDayOfWeek, startTime: `${defaultStartTime}:00`, endTime: addMinutes(defaultStartTime, 30) },
    date,
    startTime: defaultStartTime,
    timetableDayOfWeek: defaultDayOfWeek,
  };
};

const WEEKDAY_NAMES = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];

const weekdayNameFromDate = (value?: string | null): string => {
  if (!value) return '';
  const parsed = new Date(`${value}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? '' : WEEKDAY_NAMES[parsed.getDay()];
};

const formatAgeYearsMonths = (birthDate?: string | null): string => {
  if (!birthDate) return '';
  const b = new Date(`${birthDate}T00:00:00`);
  if (Number.isNaN(b.getTime())) return '';
  const now = new Date();
  let months = (now.getFullYear() - b.getFullYear()) * 12 + (now.getMonth() - b.getMonth());
  // Wenn der Tag im aktuellen Monat noch nicht erreicht wurde, einen Monat abziehen
  if (now.getDate() < b.getDate()) months -= 1;
  if (months < 0) months = 0;
  const years = Math.floor(months / 12);
  const rem = months % 12;
  return `${years} Jahre ${rem} Monate`;
};

const extractPreferredSmsContact = (
  person?: { preferredContactChannel?: unknown; preferredContactWay?: unknown; preferredContactMethod?: unknown; contactPreference?: unknown; communicationPreferences?: Record<string, unknown> | null } | null,
  subprofile?: { preferredContactChannel?: unknown; preferredContactWay?: unknown; preferredContactMethod?: unknown; contactPreference?: unknown; communications?: Array<{ type?: string; value?: string; preferred?: boolean }> | null; communicationPreferences?: Record<string, unknown> | null } | null,
): boolean => {
  const readPreferredChannel = (source: Record<string, unknown> | null | undefined): string => {
    if (!source) return '';
    const fromDirect =
      source.preferredContactChannel ??
      source.preferredContactWay ??
      source.preferredContactMethod ??
      source.contactPreference;
    if (typeof fromDirect === 'string') return fromDirect.trim().toLowerCase();
    const prefs = source.communicationPreferences;
    if (prefs && typeof prefs === 'object') {
      const p = prefs as Record<string, unknown>;
      const nested = p.preferredChannel ?? p.preferredContactChannel ?? p.channel;
      if (typeof nested === 'string') return nested.trim().toLowerCase();
    }
    return '';
  };

  const fromSubprofile = readPreferredChannel(subprofile as Record<string, unknown>);
  if (fromSubprofile === 'sms' || fromSubprofile === 'phone') return true;
  const fromPerson = readPreferredChannel(person as Record<string, unknown>);
  if (fromPerson === 'sms' || fromPerson === 'phone') return true;

  const comms = Array.isArray(subprofile?.communications) ? subprofile.communications : [];
  const hasPreferredSmsComm = comms.some((comm) => {
    const type = String(comm?.type || '').trim().toLowerCase();
    const preferred = comm?.preferred === true;
    if (!preferred) return false;
    return type === 'sms' || type === 'phone' || type.includes('sms') || type.includes('phone');
  });
  if (hasPreferredSmsComm) return true;
  const hasPreferredChannelComm = comms.some((comm) => {
    const type = String(comm?.type || '').trim().toLowerCase();
    const value = String(comm?.value || '').trim().toLowerCase();
    return (type === 'preferred_channel' || type === 'preferred_contact_channel') && (value === 'sms' || value === 'phone');
  });
  return hasPreferredChannelComm;
};

const computeFilteredRoomsForFach = (
  rooms: Room[],
  selectedRoomTags: string[],
  searchQuery: string,
): Room[] => {
  const active = rooms.filter((r) => r.aktiv);
  const query = searchQuery.trim().toLowerCase();
  const queryFiltered = query
    ? active.filter((room) => {
        const address = String(
          room.address || [room.address_street, room.address_house_number, room.address_postal_code, room.address_city].filter(Boolean).join(' ')
        ).toLowerCase();
        return room.name.toLowerCase().includes(query) || address.includes(query);
      })
    : active;
  if (selectedRoomTags.length === 0) return queryFiltered;
  const selectedNormalized = new Set(selectedRoomTags.map((tag) => tag.trim().toLowerCase()));
  return queryFiltered.filter((room) => {
    const roomTags = new Set((room.tags || []).map((tag) => String(tag).trim().toLowerCase()));
    return Array.from(selectedNormalized).every((selectedTag) => roomTags.has(selectedTag));
  });
};

const computeDefaultRoomTagsForFach = (rooms: Room[], fachId: number | undefined, fachOptions: FachItem[]): string[] => {
  if (!fachId) return [];
  const fachName = (fachOptions.find((f) => f.id === fachId)?.name || '').trim().toLowerCase();
  if (!fachName) return [];
  const tags = new Set<string>();
  rooms.forEach((room) => {
    (room.tags || []).forEach((tagRaw) => {
      const tag = String(tagRaw || '').trim();
      const normalizedTag = tag.toLowerCase();
      if (!tag) return;
      if (normalizedTag === fachName || normalizedTag.includes(fachName) || fachName.includes(normalizedTag)) {
        tags.add(tag);
      }
    });
  });
  return Array.from(tags);
};

const computeCanNext = (step: Step, flags: { step1: boolean; step2: boolean; step3: boolean; step4: boolean; step5: boolean }): boolean => {
  if (step === 1) return flags.step1;
  if (step === 2) return flags.step2;
  if (step === 3) return flags.step3;
  if (step === 4) return flags.step4;
  if (step === 5) return flags.step5;
  return false;
};

const computeDatePageModel = (
  selectedSlot: SelectedSlot,
  nextAvailableDate: string,
  datePage: number,
  datesPerPage: number,
  addDaysYmd: (ymd: string, days: number) => string
) => {
  if (!selectedSlot) return null;
  const today = toApiDate(new Date());
  const startDate = addDaysYmd(today, -56); // 8 Wochen zurück
  const endDate = addDaysYmd(today, 84); // 12 Wochen nach vorne

  const all: string[] = [];
  const start = new Date(`${startDate}T00:00:00`);
  const end = new Date(`${endDate}T00:00:00`);
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    const cand = toApiDate(d);
    const dow = ((d.getDay() + 6) % 7) + 1;
    if (dow === selectedSlot.dayOfWeek) all.push(cand);
  }

  const anchor = nextAvailableDate || today;
  const anchorIdxRaw = all.findIndex((d) => d >= anchor);
  const anchorIdx = Math.max(0, anchorIdxRaw);

  const minPage = -Math.floor(anchorIdx / datesPerPage);
  const maxPage = Math.max(minPage, Math.ceil((all.length - anchorIdx) / datesPerPage) - 1);
  const effectivePage = Math.min(maxPage, Math.max(minPage, datePage));

  const pageIndex = effectivePage - minPage;
  const totalPages = maxPage - minPage + 1;
  const startIdx = anchorIdx + effectivePage * datesPerPage;
  const pageDates = all.slice(Math.max(0, startIdx), Math.max(0, startIdx) + datesPerPage);

  return {
    anchor,
    effectivePage,
    canPrev: effectivePage > minPage,
    canNext: effectivePage < maxPage,
    pageLabel: `Seite ${pageIndex + 1} von ${totalPages}`,
    pageDates: pageDates.map((d) => ({ d, isBeforeAnchor: d < anchor, isPast: d < today })),
  };
};

const computeSelectedSlotOutsideAvailability = (selectedSlot: SelectedSlot, teacherAvailability: TeacherAvailabilitySlot[]): boolean => {
  if (!selectedSlot) return false;
  if (teacherAvailability.length === 0) return false;
  const hhmm = selectedSlot.startTime.slice(0, 5);
  const daySlots = teacherAvailability.filter((s) => Number(s.dayOfWeek) === Number(selectedSlot.dayOfWeek));
  if (daySlots.length === 0) return true;
  const toMin = (t: string) => {
    const x = t?.slice(0, 5) || '00:00';
    const [h, m] = x.split(':').map(Number);
    return (h || 0) * 60 + (m || 0);
  };
  const tMin = toMin(hhmm);
  return !daySlots.some((s) => tMin >= toMin(s.time_start) && tMin < toMin(s.time_end));
};

const computeHints = (nextAvailableLoading: boolean, nextAvailableDate: string, teacherAvailabilityLoading: boolean, hasTeacherAvailability: boolean) => {
  let step2HintText = '';
  if (nextAvailableLoading) step2HintText = 'Ermittle nächstmöglichen Termin…';
  else if (nextAvailableDate) step2HintText = `Vorschlag: ${formatDate(nextAvailableDate)}`;

  let teacherAvailabilityHint = 'Keine Freigabezeiten gepflegt – es gelten nur Belegungen/Kollisionen.';
  if (teacherAvailabilityLoading) teacherAvailabilityHint = 'Freigabezeiten werden geladen…';
  else if (hasTeacherAvailability) teacherAvailabilityHint = 'Freigabezeiten sind im Stundenplan grün hinterlegt. Slots außerhalb sind grau markiert.';
  return { step2HintText, teacherAvailabilityHint };
};

const addDaysYmd = (ymd: string, days: number): string => {
  const d = new Date(`${ymd}T00:00:00`);
  d.setDate(d.getDate() + days);
  return toApiDate(d);
};

const toDisplayTime = (value: string): string => {
  const trimmed = String(value || '').trim();
  if (!trimmed) return '';
  return trimmed.slice(0, 5);
};

const formatRoomAddress = (r?: Room | null): string => {
  if (!r) return '-';
  const line1 = [r.address_street, r.address_house_number].filter(Boolean).join(' ').trim();
  const line2 = [r.address_postal_code, r.address_city].filter(Boolean).join(' ').trim();
  const parts = [line1, line2].filter((p) => p !== '');
  if (parts.length > 0) return parts.join(', ');
  const fallback = (r.address || '').trim();
  return fallback || '-';
};

const computeCanNextFromStep4 = (kundeProfil: KundeUndProfilWert): boolean => {
  // Beim bestehenden Kunden sind die Pflichtangaben schon einmal erhoben worden;
  // sie hier erneut zu verlangen hielte jeden Altdatensatz auf.
  if (kundeProfil.personId === null && fehlendeKundenfelder(kundeProfil.kunde).length > 0) return false;
  if (kundeProfil.profilWahl.art === 'neu') {
    return fehlendeSchuelerfelder(kundeProfil.schueler, kundeProfil.kunde.phone, kundeProfil.kunde.email).length === 0;
  }
  if (kundeProfil.profilWahl.art === 'bestehend') return true;
  // „Der Kunde selbst": bei einem bestehenden Kunden gibt es dafür ein Profil,
  // bei einem neuen entsteht es beim Speichern.
  return true;
};

const computePreviewEvent = (
  selectedSlot: SelectedSlot,
  fachId: number | undefined,
  fachOptions: FachItem[],
  selectedTeilnehmer: SelectedTeilnehmerState | null
): PreviewEvent | null => {
  if (!selectedSlot || !fachId) return null;
  const fachName = fachOptions.find((f) => f.id === fachId)?.name || 'Probestunde';
  const [hh, mm] = selectedSlot.startTime.slice(0, 5).split(':').map(Number);
  const totalMinutes = hh * 60 + mm + 30;
  const endH = Math.floor(totalMinutes / 60) % 24;
  const endM = totalMinutes % 60;
  const endTime = `${String(endH).padStart(2, '0')}:${String(endM).padStart(2, '0')}`;
  return {
    dayOfWeek: selectedSlot.dayOfWeek,
    startTime: selectedSlot.startTime.slice(0, 5),
    endTime,
    title: 'Probestunde',
    subtitle: selectedTeilnehmer?.name || 'Schüler',
    fachName,
    color: 'bg-amber-200 border-amber-600 text-amber-950',
  };
};

const computeParticipantLabelForHistory = (
  selectedTeilnehmer: SelectedTeilnehmerState | null,
  trialRequestPerson?: { companyName?: string; firstName?: string; lastName?: string }
) => {
  const fromSelected = (selectedTeilnehmer?.name || '').trim();
  if (fromSelected) return fromSelected;
  const companyName = (trialRequestPerson?.companyName || '').trim();
  if (companyName) return companyName;
  const fn = (trialRequestPerson?.firstName || '').trim();
  const ln = (trialRequestPerson?.lastName || '').trim();
  return `${fn} ${ln}`.trim();
};

const computeCreateHistoryText = (participantLabelForHistory: string, selectedStandortName?: string) => {
  const standortName = (selectedStandortName || '').trim();
  if (!participantLabelForHistory && !standortName) return 'Probestunde angelegt';
  if (!participantLabelForHistory) return `Probestunde angelegt (${standortName})`;
  const locationSuffix = standortName ? ` (${standortName})` : '';
  return `Probestunde für ${participantLabelForHistory} angelegt${locationSuffix}`;
};

const getTeacherDisplayName = (options: LehrerOption[], teacherId?: number): string => {
  if (!teacherId) return '';
  const teacher = options.find((t) => t.id === teacherId);
  return teacher ? `${teacher.vorname} ${teacher.nachname}` : '';
};

const parseSubprofileId = (rawId?: string): { wpUserId: number; subIndex: number | null } => {
  const subId = String(rawId || '').trim();
  if (!subId) return { wpUserId: 0, subIndex: null };
  if (subId.includes('-')) {
    const [parentRaw, subRaw] = subId.split('-', 2);
    return {
      wpUserId: Number.parseInt(parentRaw, 10) || 0,
      subIndex: subRaw ? (Number.parseInt(subRaw, 10) || null) : null,
    };
  }
  return { wpUserId: Number.parseInt(subId, 10) || 0, subIndex: null };
};

const buildComposerGroups = (params: {
  trialRequestId?: number;
  trialRequestPerson?: { salutation?: string; companyName?: string; firstName?: string; lastName?: string };
  selectedTeilnehmer: { id: string; name: string; email?: string | null; phone?: string | null } | null;
  teacherId?: number;
  teacherDisplayName: string;
  date: string;
  startTime: string;
  endTime: string;
  roomName: string;
  fachName: string;
  birthDate: string;
  studentSmsPreferred: boolean;
}): RecipientGroup[] => {
  const {
    trialRequestId,
    trialRequestPerson,
    selectedTeilnehmer,
    teacherId,
    teacherDisplayName,
    date,
    startTime,
    endTime,
    roomName,
    fachName,
    birthDate,
    studentSmsPreferred,
  } = params;

  const groups: RecipientGroup[] = [];
  const requestCompanyName = (trialRequestPerson?.companyName || '').trim();
  const requestContactName = `${trialRequestPerson?.firstName || ''} ${trialRequestPerson?.lastName || ''}`.trim();
  const studentName = selectedTeilnehmer?.name || requestCompanyName || requestContactName;
  const roomClause = roomName ? ` im Raum ${roomName}` : '';
  const [studentFirstName, ...studentRest] = studentName.split(' ').filter(Boolean);
  const teacherFirstName = (teacherDisplayName.split(' ').filter(Boolean)[0] || '').trim();
  const sharedVars = {
    studentName,
    teacherName: teacherDisplayName,
    date,
    startTime,
    endTime,
    roomName,
    roomClause,
    // Namen, wie die aus dem Altsystem übernommenen Vorlagen sie ansprechen.
    studentFirstName: studentFirstName || studentName,
    studentLastName: studentRest.join(' '),
    teacherFirstName: teacherFirstName || teacherDisplayName,
    fachName,
    weekdayName: weekdayNameFromDate(date),
    studentAge: formatAgeYearsMonths(birthDate),
  };

  const tid = teacherId ? Number(teacherId) : 0;
  if (tid) {
    const tName = teacherDisplayName || `Lehrer #${tid}`;
    groups.push({
      key: 'teacher',
      label: 'Lehrer',
      recipients: [{ id: `person:${tid}:`, type: 'person' as const, wpUserId: tid, subIndex: null, name: tName }],
      templateKeys: { email: 'trial.created.teacher', sms: 'trial.created.teacher' },
      vars: sharedVars,
      defaultSelectedChannels: { email: true, sms: true },
    });
  }

  if (trialRequestId) {
    const name = requestCompanyName || requestContactName || 'Teilnehmer';
    groups.push({
      key: 'student',
      label: 'Teilnehmer',
      recipients: [{ id: `trial_request:${trialRequestId}`, type: 'trial_request' as const, trialRequestId, name }],
      templateKeys: { email: 'trial.created.student', sms: 'trial.created.student' },
      vars: sharedVars,
      defaultSelectedChannels: { email: true, sms: studentSmsPreferred },
    });
  } else {
    const subId = String(selectedTeilnehmer?.id || '').trim();
    if (subId) {
      const { wpUserId, subIndex } = parseSubprofileId(subId);
      if (wpUserId) {
        groups.push({
          key: 'student',
          label: 'Teilnehmer',
          recipients: [{
            id: `person:${wpUserId}:${subIndex ?? ''}`,
            type: 'person' as const,
            wpUserId,
            subIndex,
            name: selectedTeilnehmer?.name || 'Teilnehmer',
            // Im Formular geändert, aber noch nicht gespeichert (T-0031).
            email: selectedTeilnehmer?.email ?? null,
            phone: selectedTeilnehmer?.phone ?? null,
          }],
          templateKeys: { email: 'trial.created.student', sms: 'trial.created.student' },
          vars: sharedVars,
          defaultSelectedChannels: { email: true, sms: studentSmsPreferred },
        });
      }
    }
  }

  return groups;
};

const getNextStepError = (params: {
  step: Step;
  canNextFromStep1: boolean;
  canNextFromStep2: boolean;
  canNextFromStep3: boolean;
  canNextFromStep4: boolean;
}): string | undefined => {
  const { step, canNextFromStep1, canNextFromStep2, canNextFromStep3, canNextFromStep4 } = params;
  const errorByStep: Partial<Record<Step, string>> = {
    1: canNextFromStep1 ? undefined : `Bitte ${getSubjectLabels().singular}, Lehrer und einen Slot wählen`,
    2: canNextFromStep2 ? undefined : `Bitte ${getSubjectLabels().singular} und Datum wählen`,
    3: canNextFromStep3 ? undefined : 'Bitte Raum wählen',
    4: canNextFromStep4 ? undefined : 'Bitte Pflichtfelder prüfen (Anrede, Ansprechpartner, Geburtsdatum, gültige E-Mail/Telefon; bei Firma zusätzlich Firmenname)',
  };
  return errorByStep[step];
};

const TrialWizardModal: React.FC<TrialWizardModalProps> = ({
  open,
  onOpenChange,
  defaultFachId,
  defaultTeacherId,
  lockTeacher = false,
  requireDateSelection = false,
  trialRequestId,
  trialRequestPerson,
  defaultDate,
  defaultStartTime,
  defaultDayOfWeek,
  defaultRoomId,
  defaultCourseId,
  defaultSubprofileId,
  defaultParentPersonId,
  onCreated,
}) => {
  const { selectedStandortId, selectedStandort } = useStandort();
  const [step, setStep] = useState<Step>(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [step1ActiveSection, setStep1ActiveSection] = useState<Step1Section>('unterricht');
  const step1UnterrichtCollapsed = step1ActiveSection !== 'unterricht';
  const step1TerminplanCollapsed = step1ActiveSection !== 'terminplan';
  // Step 1 (Terminplan/Stundenplan): stabiler Referenztag für mobile Tagesansicht + Wochentagswechsel
  const [timetableBaseDate, setTimetableBaseDate] = useState<Date>(new Date());
  const [timetableDayOfWeek, setTimetableDayOfWeek] = useState<number>(() => {
    const jsDow = new Date().getDay();
    return jsDow === 0 ? 7 : jsDow; // 1..7 (Mo=1)
  });
  // Step 1
  const [fachOptions, setFachOptions] = useState<FachItem[]>([]);
  const [fachId, setFachId] = useState<number | undefined>(defaultFachId);
  const [teacherOptions, setTeacherOptionsState] = useState<LehrerOption[]>([]);
  // Für welches Fach/welchen Standort die Lehrerliste geladen ist. Solange sie
  // noch nicht da ist, darf die vorgewählte Lehrkraft nicht als „nicht mehr
  // verfügbar“ verworfen werden.
  const [teacherOptionsFor, setTeacherOptionsFor] = useState<string | null>(null);
  const [teacherId, setTeacherId] = useState<number | undefined>(defaultTeacherId ? Number(defaultTeacherId) : undefined);
  const [teacherAvailability, setTeacherAvailability] = useState<TeacherAvailabilitySlot[]>([]);
  const [teacherAvailabilityLoading, setTeacherAvailabilityLoading] = useState(false);
  const [date, setDate] = useState<string>(defaultDate || '');
  const [startTime, setStartTime] = useState<string>(defaultStartTime || '');
  const endTime = useMemo(() => (startTime ? addMinutes(startTime, 30) : ''), [startTime]);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [roomId, setRoomId] = useState<number | undefined>(defaultRoomId);
  const [teacherUnits, setTeacherUnits] = useState<UnterrichtseinheitDTO[]>([]);
  const [teacherDayTrials, setTeacherDayTrials] = useState<TrialLessonDTO[]>([]);
  const [selectedSlot, setSelectedSlot] = useState<SelectedSlot>(null);
  const [selectedCourseId, setSelectedCourseId] = useState<number | undefined>();
  const [selectedCourse, setSelectedCourse] = useState<KursItem | null>(null);

  // Step 2: Paginierte Terminauswahl
  const [datePage, setDatePage] = useState<number>(0);
  const datesPerPage = 4;
  const [nextAvailableDate, setNextAvailableDate] = useState<string>('');
  const [nextAvailableLoading, setNextAvailableLoading] = useState<boolean>(false);

  // Step 3: Raumauswahl
  const [selectedRoomTags, setSelectedRoomTags] = useState<string[]>([]);
  const [roomTagsUserModified, setRoomTagsUserModified] = useState<boolean>(false);
  const [roomSearchQuery, setRoomSearchQuery] = useState<string>('');
  const [roomUnitsForDate, setRoomUnitsForDate] = useState<RoomUnit[]>([]);
  const [roomUnitsLoading, setRoomUnitsLoading] = useState(false);
  const [roomUnitsError, setRoomUnitsError] = useState<string>('');
  const [roomPreviewOpen, setRoomPreviewOpen] = useState(false);
  const [roomPreview, setRoomPreview] = useState<Room | null>(null);
  const [coursePickOpen, setCoursePickOpen] = useState(false);
  const [coursePickLoading, setCoursePickLoading] = useState(false);
  const [coursePick, setCoursePick] = useState<{ courseId: number; date: string; dayOfWeek: number; startTime: string; endTime: string; course?: KursItem | null } | null>(null);
  const [teacherDayLoading, setTeacherDayLoading] = useState(false);
  const [teacherDayError, setTeacherDayError] = useState<string>('');

  // Step 4: Teilnehmer — Kunde und Schülerprofil in einer Auswahl (T-0007).
  // Vorher lagen hier zwölf Einzelzustände nebeneinander: eine Ja/Nein-Frage,
  // eine Profilsuche, ein Kurzformular mit drei Feldern und ein zweites mit
  // allen Kundenangaben. Welcher davon galt, hing am Weg in den Assistenten.
  const [kundeProfil, setKundeProfil] = useState<KundeUndProfilWert>(LEERER_WERT);
  const [teilnehmerGeprueft, setTeilnehmerGeprueft] = useState<boolean>(false);
  /**
   * Was im Kommunikationsschritt steht. Bisher wurde es angezeigt und beim
   * Speichern verworfen — der Server rendert die Vorlage dann ein zweites Mal,
   * ohne die Änderungen (T-0032). Dieselbe Falle wie in T-0004.
   */
  const [mailEntwuerfe, setMailEntwuerfe] = useState<
    Record<string, { subject: string; html: string; text: string; sms: string }>
  >({});

  // Was der Rest des Assistenten bisher aus zwölf Zuständen zusammensuchte,
  // steht jetzt in einem: Name und Kennung des Teilnehmers, und die Angaben des
  // Kunden für die Umwandlung.
  const gewaehltesProfil = useMemo(() => gewaehlteProfilId(kundeProfil), [kundeProfil]);
  const teilnehmerName = useMemo(() => {
    if (kundeProfil.profilWahl.art === 'neu') {
      return [kundeProfil.schueler.firstName, kundeProfil.schueler.lastName].filter(Boolean).join(' ').trim();
    }
    const gewaehlt = kundeProfil.profile.find((profil: ProfilZeile) => profil.id === gewaehltesProfil);
    return (
      gewaehlt?.name
      || [kundeProfil.kunde.firstName, kundeProfil.kunde.lastName].filter(Boolean).join(' ').trim()
    );
  }, [kundeProfil, gewaehltesProfil]);
  const selectedTeilnehmer = useMemo<SelectedTeilnehmerState | null>(() => {
    if (!gewaehltesProfil && !teilnehmerName) return null;
    return {
      id: gewaehltesProfil,
      name: teilnehmerName,
      parentId: kundeProfil.personId ? String(kundeProfil.personId) : undefined,
      email: kundeProfil.kunde.email || null,
      phone: kundeProfil.kunde.phone || null,
      birthDate:
        kundeProfil.profilWahl.art === 'neu'
          ? kundeProfil.schueler.birthDate || null
          : kundeProfil.profile.find((profil: ProfilZeile) => profil.id === gewaehltesProfil)?.birthDate || null,
    };
  }, [gewaehltesProfil, teilnehmerName, kundeProfil]);
  const trialParticipantDraft = useMemo<TrialParticipantDraft>(() => {
    const adresse = kundeProfil.kunde.structuredAddress || {};
    return {
      salutation: (kundeProfil.kunde.salutation || '') as TrialParticipantDraft['salutation'],
      isCompany: kundeProfil.kunde.isCompany,
      companyName: kundeProfil.kunde.companyName,
      firstName: kundeProfil.kunde.firstName,
      lastName: kundeProfil.kunde.lastName,
      birthDate: kundeProfil.kunde.birthDate || '',
      street: String(adresse.street || ''),
      postalCode: String(adresse.zip || adresse.postalCode || ''),
      city: String(adresse.city || ''),
      country: String(adresse.country || ''),
      email: kundeProfil.kunde.email,
      phone: kundeProfil.kunde.phone,
    };
  }, [kundeProfil.kunde]);
  const [studentSmsPreferred, setStudentSmsPreferred] = useState<boolean>(false);

  // Step 5: Kommunikation
  const [skipStudentEmail, setSkipStudentEmail] = useState<boolean>(false);
  const [skipTeacherEmail, setSkipTeacherEmail] = useState<boolean>(false);
  const [skipStudentSms, setSkipStudentSms] = useState<boolean>(false);
  const [skipTeacherSms, setSkipTeacherSms] = useState<boolean>(false);
  const sendEmails = useMemo(() => !skipStudentEmail || !skipTeacherEmail, [skipStudentEmail, skipTeacherEmail]);
  const sendSms = useMemo(() => !skipStudentSms || !skipTeacherSms, [skipStudentSms, skipTeacherSms]);
  const teacherOptionsKey = `${fachId ?? ''}:${selectedStandortId ?? ''}`;
  const teacherOptionsKeyRef = useRef(teacherOptionsKey);
  teacherOptionsKeyRef.current = teacherOptionsKey;
  const setTeacherOptions = useCallback((list: LehrerOption[]) => {
    setTeacherOptionsState(list);
    setTeacherOptionsFor(teacherOptionsKeyRef.current);
  }, []);
  const teacherDisplayName = useMemo(() => getTeacherDisplayName(teacherOptions, teacherId), [teacherOptions, teacherId]);

  // Composer groups for step 5 (student + teacher communication)
  const composerGroups: RecipientGroup[] = useMemo(() => {
    const roomName = roomId ? rooms.find((r) => r.id === roomId)?.name || '' : '';
    return buildComposerGroups({
      trialRequestId,
      trialRequestPerson,
      selectedTeilnehmer: selectedTeilnehmer
        ? {
            id: selectedTeilnehmer.id,
            name: selectedTeilnehmer.name,
            email: selectedTeilnehmer.email,
            phone: selectedTeilnehmer.phone,
          }
        : null,
      teacherId,
      teacherDisplayName,
      date,
      startTime,
      endTime,
      roomName,
      fachName: fachOptions.find((f) => f.id === fachId)?.name || '',
      birthDate: geburtsdatumFuerMail(selectedTeilnehmer?.birthDate, gewaehltesProfil, trialParticipantDraft.birthDate),
      studentSmsPreferred,
    });
  }, [trialRequestId, trialRequestPerson, selectedTeilnehmer, gewaehltesProfil, teacherId, teacherDisplayName, date, startTime, endTime, roomId, rooms, fachId, fachOptions, trialParticipantDraft.birthDate, studentSmsPreferred]);

  // Step 6: Event-Historie (Preview + editierbar)
  const [createHistoryText, setCreateHistoryText] = useState<string>('');
  const [createHistoryTouched, setCreateHistoryTouched] = useState<boolean>(false);
  const [historyTaskPreview, setHistoryTaskPreview] = useState<{ enabled: boolean; description?: string; dueAt?: string } | null>(null);

  // Load master data on open
  useEffect(() => {
    if (!open) return;
    const initialSlot = buildInitialSlotFromDefaults({
      defaultStartTime,
      defaultDate,
      defaultDayOfWeek,
      requireDateSelection,
    });

    setStep1ActiveSection('unterricht');
    setTimetableBaseDate(new Date());
    setTimetableDayOfWeek(initialSlot.timetableDayOfWeek);
    setStep(requireDateSelection ? 2 : 1);
    setDatePage(0);
    setSelectedRoomTags([]);
    setRoomTagsUserModified(false);
    setRoomSearchQuery('');
    // Reset states with defaults
    setFachId(defaultFachId);
    setTeacherOptionsFor(null);
    setTeacherId(defaultTeacherId ? Number(defaultTeacherId) : undefined);
    setDate(initialSlot.date || '');
    setStartTime(initialSlot.startTime || '');
    setRoomId(defaultRoomId);
    // Die Vorbelegung des Teilnehmers: aus der Anfrage, oder aus einem Lernenden,
    // den der Terminplan schon mitbringt. Den Kunden dazu lädt die Auswahl selbst.
    const anrede = String(trialRequestPerson?.salutation || '').trim();
    const firma = String(trialRequestPerson?.companyName || '').trim();
    const vorgabeProfil = String(defaultSubprofileId || trialRequestPerson?.subprofileId || '').trim();
    const vorgabeKunde = Number(defaultParentPersonId || trialRequestPerson?.personId || 0);
    setKundeProfil({
      ...LEERER_WERT,
      personId: vorgabeKunde > 0 ? vorgabeKunde : null,
      kunde: {
        ...LEERE_KUNDENDATEN,
        salutation: anrede === 'Firma' ? '' : anrede,
        isCompany: anrede === 'Firma' || firma !== '',
        companyName: firma,
        firstName: String(trialRequestPerson?.firstName || '').trim(),
        lastName: String(trialRequestPerson?.lastName || '').trim(),
        birthDate: String(trialRequestPerson?.birthDate || '').trim(),
        email: String(trialRequestPerson?.email || '').trim(),
        phone: String(trialRequestPerson?.phone || '').trim(),
      },
      profilWahl: vorgabeProfil ? { art: 'bestehend', subprofileId: vorgabeProfil } : { art: 'konto' },
    });
    setTeilnehmerGeprueft(false);
    setStudentSmsPreferred(false);
    setSelectedSlot(null);
    setSelectedCourseId(defaultCourseId || undefined);
    setSelectedCourse(null);
    setSkipStudentEmail(false);
    setSkipTeacherEmail(false);
    setSkipStudentSms(false);
    setSkipTeacherSms(false);
    setCreateHistoryText('');
    setCreateHistoryTouched(false);
    setHistoryTaskPreview(null);
    setSelectedSlot(initialSlot.slot);
    
    // Steht die Lehrkraft fest (Terminplan), nur ihre Fächer anbieten (T-0027).
    const fachQuelle = lockTeacher && defaultTeacherId
      ? lehrerApi.getAvailableFaecher(Number(defaultTeacherId))
      : masterdataApi.faecher();
    fachQuelle
      .then((items) => {
        const liste = items as FachItem[];
        setFachOptions(liste);
        // Aus dem Terminkalender startet der Assistent bei Schritt 2 und lässt
        // die Fachwahl aus. Unterrichtet die Lehrkraft nur ein Fach, steht es
        // damit fest (T-0308).
        if (!defaultFachId && liste.length === 1) setFachId(liste[0].id);
      })
      .catch(() => setFachOptions([]));
  }, [open, lockTeacher, requireDateSelection, defaultDayOfWeek, defaultStartTime, defaultDate, defaultFachId, defaultTeacherId, defaultRoomId, defaultCourseId, defaultSubprofileId, defaultParentPersonId, trialRequestPerson]);

  useEffect(() => {
    if (!open || !trialRequestId) return;
    // Nachladen, solange die Verknüpfung fehlt — die Listen-Daten führen sie
    // nicht immer mit, und ohne sie liefe die Umwandlung in einen Konflikt.
    if (kundeProfil.personId !== null) return;
    let cancelled = false;
    void trialRequestsApi
      .get(trialRequestId)
      .then((req) => {
        if (cancelled) return;
        // Die Personennummer des Kontoinhabers, nicht die Portal-Kennung — mit
        // der liefen Personenakte und „Kunde sichern" ins Leere (T-0059).
        const personId = Number(req?.existing_person_id || 0);
        const subprofileId = String(req?.subprofile_id || '').trim();
        if (personId <= 0) return;
        setKundeProfil((prev: KundeUndProfilWert) => ({
          ...prev,
          personId,
          profilWahl: subprofileId ? { art: 'bestehend', subprofileId } : prev.profilWahl,
        }));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [open, trialRequestId, kundeProfil.personId]);

  // Ob der Lernende lieber eine SMS bekommt, steht an Person und Profil. Die
  // Stammdaten selbst lädt die Teilnehmer-Auswahl; hier bleibt nur die Vorliebe.
  useEffect(() => {
    if (!open) return;
    const wpUserId = Number(kundeProfil.personId || 0);
    if (wpUserId <= 0) {
      setStudentSmsPreferred(false);
      return;
    }
    let cancelled = false;
    const laden = async () => {
      try {
        const subprofileId = gewaehlteProfilId(kundeProfil);
        const [person, subprofile] = await Promise.all([
          api.get<Record<string, unknown>>(`/api/personen/${wpUserId}`),
          subprofileId
            ? api.get<Record<string, unknown>>(`/api/personen/${wpUserId}/subprofiles/${subprofileId}`)
            : Promise.resolve(null),
        ]);
        if (cancelled) return;
        setStudentSmsPreferred(extractPreferredSmsContact(person, subprofile));
      } catch {
        setStudentSmsPreferred(false);
      }
    };
    void laden();
    return () => {
      cancelled = true;
    };
  }, [open, kundeProfil]);

  useEffect(() => {
    if (!open || trialRequestId) return;
    const subId = String(selectedTeilnehmer?.id || '').trim();
    if (!subId) {
      setStudentSmsPreferred(false);
      return;
    }
    const { wpUserId, subIndex } = parseSubprofileId(subId);
    if (wpUserId <= 0) {
      setStudentSmsPreferred(false);
      return;
    }
    let cancelled = false;
    const loadPreferences = async () => {
      try {
        type LinkedPerson = {
          preferredContactChannel?: string | null;
          preferredContactWay?: string | null;
          preferredContactMethod?: string | null;
          contactPreference?: string | null;
          communicationPreferences?: Record<string, unknown> | null;
        };
        type LinkedSubprofile = {
          preferredContactChannel?: string | null;
          preferredContactWay?: string | null;
          preferredContactMethod?: string | null;
          contactPreference?: string | null;
          communicationPreferences?: Record<string, unknown> | null;
          communications?: Array<{ type?: string; value?: string; preferred?: boolean }>;
        };
        const personPromise = api.get<LinkedPerson>(`/api/personen/${wpUserId}`);
        const subprofilePromise =
          subIndex !== null
            ? api.get<LinkedSubprofile>(`/api/personen/${wpUserId}/subprofiles/${wpUserId}-${subIndex}`)
            : Promise.resolve(null);
        const [person, subprofile] = await Promise.all([personPromise, subprofilePromise]);
        if (cancelled) return;
        setStudentSmsPreferred(extractPreferredSmsContact(person, subprofile));
      } catch {
        if (!cancelled) setStudentSmsPreferred(false);
      }
    };
    void loadPreferences();
    return () => {
      cancelled = true;
    };
  }, [open, trialRequestId, selectedTeilnehmer?.id]);

  useEffect(() => {
    let cancelled = false;
    const renderTemplate = async () => {
      if (!open || createHistoryTouched) return;
      const studentName = selectedTeilnehmer?.name || (trialRequestPerson ? `${trialRequestPerson.firstName || ''} ${trialRequestPerson.lastName || ''}`.trim() : '');
      if (!studentName || !date || !startTime || !endTime) return;
      try {
        const roomName = rooms.find((r) => r.id === roomId)?.name || '';
        const displayDate = formatDate(date);
        const displayStartTime = toDisplayTime(startTime);
        const displayEndTime = toDisplayTime(endTime);
        const rendered = await historyTemplatesApi.render({
          template_key: 'trial.created.history',
          vars: {
            studentName,
            date: displayDate,
            startTime: displayStartTime,
            endTime: displayEndTime,
            dateIso: date,
            startTimeRaw: startTime,
            endTimeRaw: endTime,
            roomName,
            roomClause: roomName ? ` im Raum ${roomName}` : '',
          },
        });
        if (cancelled) return;
        if (rendered.history) setCreateHistoryText(rendered.history);
        if (rendered.task) {
          setHistoryTaskPreview({ enabled: true, description: rendered.task.description, dueAt: rendered.task.dueAt });
        } else {
          setHistoryTaskPreview({ enabled: false });
        }
      } catch {
        if (!cancelled) setHistoryTaskPreview({ enabled: false });
      }
    };
    void renderTemplate();
    return () => {
      cancelled = true;
    };
  }, [open, createHistoryTouched, selectedTeilnehmer, trialRequestPerson, date, startTime, endTime, rooms, roomId]);

  const allRoomTags = useMemo(() => {
    const tagSet = new Set<string>();
    rooms
      .filter((room) => room.aktiv)
      .forEach((room) => {
        (room.tags || []).forEach((tagRaw) => {
          const tag = String(tagRaw || '').trim();
          if (tag) tagSet.add(tag);
        });
      });
    return Array.from(tagSet).sort((a, b) => a.localeCompare(b, 'de'));
  }, [rooms]);
  const filteredRoomsForFach = useMemo(
    () => computeFilteredRoomsForFach(rooms, selectedRoomTags, roomSearchQuery),
    [rooms, selectedRoomTags, roomSearchQuery]
  );

  useEffect(() => {
    if (!open || roomTagsUserModified) return;
    setSelectedRoomTags(computeDefaultRoomTagsForFach(rooms, fachId, fachOptions));
  }, [open, rooms, fachId, fachOptions, roomTagsUserModified]);

  const hasTeacherCollision = useMemo(() => {
    if (!date || !startTime) return false;
    const end = endTime || addMinutes(startTime, 30);
    const unitOverlap = teacherUnits.some(u => overlaps(startTime, end, u.start_zeit.slice(11, 16), u.end_zeit.slice(11, 16)));
    const trialOverlap = teacherDayTrials.some(t => overlaps(startTime, end, t.startTime.slice(0,5), t.endTime.slice(0,5)));
    return unitOverlap || trialOverlap;
  }, [teacherUnits, teacherDayTrials, date, startTime, endTime]);

  const teacherRoomsOnSelectedDay = useMemo(() => {
    const set = new Set<number>();
    teacherUnits.forEach((u) => {
      const id = Number(u.raum_id ?? 0);
      if (Number.isFinite(id) && id > 0) set.add(id);
    });
    teacherDayTrials.forEach((t) => {
      const id = Number(t.roomId ?? 0);
      if (Number.isFinite(id) && id > 0) set.add(id);
    });
    return set;
  }, [teacherUnits, teacherDayTrials]);
  const canNextFromStep1 = useMemo(() => !!(fachId && teacherId && selectedSlot), [fachId, teacherId, selectedSlot]);
  const canNextFromStep2 = useMemo(
    () => !!(fachId && date && startTime && !hasTeacherCollision),
    [fachId, date, startTime, hasTeacherCollision]
  );
  const canNextFromStep3 = useMemo(() => !!roomId, [roomId]);
  const canNextFromStep4 = useMemo(() => computeCanNextFromStep4(kundeProfil), [kundeProfil]);
  const canNextFromStep5 = true;

  const canNext = useMemo(
    () => computeCanNext(step, { step1: canNextFromStep1, step2: canNextFromStep2, step3: canNextFromStep3, step4: canNextFromStep4, step5: canNextFromStep5 }),
    [step, canNextFromStep1, canNextFromStep2, canNextFromStep3, canNextFromStep4, canNextFromStep5]
  );

  const datePageModel = useMemo(
    () => computeDatePageModel(selectedSlot, nextAvailableDate, datePage, datesPerPage, addDaysYmd),
    [selectedSlot, nextAvailableDate, datePage, datesPerPage]
  );

  const canSubmit = useMemo(
    () => Boolean(fachId && teacherId && date && startTime && canNextFromStep4),
    [fachId, teacherId, date, startTime, canNextFromStep4]
  );

  const selectedSubjectName = fachOptions.find((f) => f.id === fachId)?.name || '—';
  const teacherSummaryName = teacherDisplayName || (teacherId ? `#${teacherId}` : '—');
  const missingScheduleSelectionText = fachId ? 'Bitte zuerst einen Lehrer auswählen.' : `Bitte zuerst ${getSubjectLabels().singular} auswählen.`;
  const canShowTerminplan = Boolean(fachId && teacherId);
  const hasTeacherAvailability = teacherAvailability.length > 0;
  const { step2HintText, teacherAvailabilityHint } = computeHints(nextAvailableLoading, nextAvailableDate, teacherAvailabilityLoading, hasTeacherAvailability);

  const timetableReferenceDate = useMemo(() => {
    const base = timetableBaseDate;
    const jsDow = base.getDay(); // 0..6 (So..Sa)
    const baseDow = jsDow === 0 ? 7 : jsDow; // 1..7 (Mo..So)
    const diff = Number(timetableDayOfWeek || baseDow) - baseDow;
    const d = new Date(base);
    d.setDate(base.getDate() + diff);
    return d;
  }, [timetableBaseDate, timetableDayOfWeek]);

  // PreviewEvent für ausgewählten Slot erstellen
  const previewEvent: PreviewEvent | null = useMemo(
    () => computePreviewEvent(selectedSlot, fachId, fachOptions, selectedTeilnehmer),
    [selectedSlot, fachId, fachOptions, selectedTeilnehmer]
  );

  const selectedSlotOutsideAvailability = useMemo(
    () => computeSelectedSlotOutsideAvailability(selectedSlot, teacherAvailability),
    [selectedSlot, teacherAvailability]
  );
  const showOutsideAvailabilityWarning = Boolean(selectedSlot && hasTeacherAvailability && selectedSlotOutsideAvailability);

  // Hinweis: Früher wurden hier Default-E-Mail Texte in lokale Textareas geschrieben.
  // Nach Umstellung auf CommunicationComposer ist das nicht mehr nötig (Templates/VARS werden dort gerendert).

  const participantLabelForHistory = useMemo(
    () => computeParticipantLabelForHistory(selectedTeilnehmer, trialRequestPerson),
    [selectedTeilnehmer, trialRequestPerson]
  );

  const computedCreateHistoryText = useMemo(
    () => computeCreateHistoryText(participantLabelForHistory, selectedStandort?.name),
    [participantLabelForHistory, selectedStandort?.name]
  );

  useTrialWizardEffects({
    open,
    step,
    fachId,
    teacherId,
    selectedCourseId,
    selectedStandortId: selectedStandortId ?? undefined,
    date,
    rooms,
    selectedSlot,
    nextAvailableDate,
    teacherUnits,
    teacherDayTrials,
    roomId,
    selectedTeilnehmer,
    createHistoryTouched,
    computedCreateHistoryText,
    setStep1ActiveSection,
    setSelectedCourse,
    setTeacherOptions,
    setRooms,
    setRoomUnitsForDate,
    setRoomUnitsLoading,
    setRoomUnitsError,
    setTeacherAvailability,
    setTeacherAvailabilityLoading,
    setTeacherUnits,
    setTeacherDayTrials,
    setTeacherDayLoading,
    setTeacherDayError,
    setNextAvailableDate,
    setNextAvailableLoading,
    setDate,
    setStartTime,
    setRoomId,
    setCreateHistoryText,
    addMinutes,
    overlaps,
    apiClient: api,
  });

  useEffect(() => {
    if (!open || lockTeacher || !teacherId) return;
    if (teacherOptionsFor !== teacherOptionsKey) return;
    const teacherStillAvailable = teacherOptions.some((teacher) => teacher.id === teacherId);
    if (teacherStillAvailable) return;
    setTeacherId(undefined);
    setSelectedSlot(null);
    setSelectedCourseId(undefined);
    setSelectedCourse(null);
    setDate('');
    setStartTime('');
  }, [open, lockTeacher, teacherId, teacherOptions, teacherOptionsFor, teacherOptionsKey]);

  /**
   * Kunde und Schülerprofil festschreiben, bevor die Probestunde entsteht.
   *
   * Beide Wege — von Hand anlegen und Anfrage umwandeln — brauchen am Ende eine
   * Profil-Kennung. Vorher legte jeder seinen Datensatz selbst an, mit
   * unterschiedlichem Ergebnis.
   */
  const teilnehmerFestschreiben = async (): Promise<string | null> => {
    try {
      const gesichert = await stammdatenApi.sichern({
        personId: kundeProfil.personId,
        kunde: kundeProfil.kunde,
        schueler: kundeProfil.profilWahl.art === 'neu' ? kundeProfil.schueler : undefined,
        profilWahl: kundeProfil.profilWahl.art,
        subprofileId: gewaehltesProfil || undefined,
        standortId: selectedStandortId ?? null,
      });
      setKundeProfil((prev: KundeUndProfilWert) => ({
        ...prev,
        personId: gesichert.personId,
        profilWahl: { art: 'bestehend', subprofileId: gesichert.subprofileId },
      }));
      return gesichert.subprofileId;
    } catch (e: any) {
      toast.error(e?.message || 'Der Teilnehmer konnte nicht gespeichert werden');
      return null;
    }
  };

  // Handler für Kursauswahl durch Klick
  const handleSelectEvent = async (event: TeacherTimetableSelectEvent) => {
    const resolved = await resolveCoursePickFromEventAction({ event, addMinutes });
    if (!resolved) return;
    setCoursePickLoading(true);
    setCoursePickOpen(true);
    try {
      setCoursePick(resolved.coursePick);
      if (resolved.hadLoadError) toast.error('Kurs konnte nicht geladen werden');
    } finally { 
      setCoursePickLoading(false);
    }
  };

  const submit = async () => {
    setTeilnehmerGeprueft(true);
    try {
      setIsSubmitting(true);
      const subprofileId = await teilnehmerFestschreiben();
      if (!subprofileId) return;
      const result = await submitTrialWizardAction({
        fachId,
        teacherId,
        date,
        startTime,
        endTime,
        roomId,
        sendEmails,
        skipStudentEmail,
        skipTeacherEmail,
        sendSms,
        skipStudentSms,
        skipTeacherSms,
        trialRequestId,
        trialParticipantDraft,
        subprofileId,
        teilnehmerName,
        selectedCourseId,
        selectedStandortId: selectedStandortId ?? undefined,
        createHistoryText,
        historyTemplateKey: 'trial.created.history',
        mailEntwuerfe,
      });
      if (!result) {
        toast.error('Die Probestunde konnte nicht angelegt werden');
        return;
      }
      const createdData = (result.created ?? {}) as Record<string, unknown>;
      // Schon umgewandelt (z. B. aus einem zweiten Tab): nichts Neues angelegt (T-0166).
      if ("alreadyConverted" in result && result.alreadyConverted) {
        toast.info('Diese Anfrage wurde bereits umgewandelt — es wurde keine neue Probestunde angelegt.');
        onOpenChange(false);
        if (onCreated) onCreated(result.created);
        return;
      }
      const emailError = typeof createdData.emailError === 'string' ? createdData.emailError.trim() : '';
      const smsError = typeof createdData.smsError === 'string' ? createdData.smsError.trim() : '';
      if (emailError || smsError) {
        // Beide Gründe nennen: Wer nur den ersten sieht, sucht den zweiten nicht (T-0020).
        toast.error(
          `Probestunde angelegt, aber der Versand klemmt — ${[emailError, smsError].filter(Boolean).join(' ')}`
        );
      } else {
        toast.success('Probestunde angelegt');
      }
      onOpenChange(false);
      if (onCreated) onCreated(result.created);
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Anlegen fehlgeschlagen');
    } finally {
      setIsSubmitting(false);
    }
  };

  const modalContentClassName = useMemo(
    () => (step === 1 ? 'w-[98vw] h-[96vh] max-w-[98vw] max-h-[96vh]' : undefined),
    [step]
  );

  return (
    <WizardShell
      open={open}
      onOpenChange={onOpenChange}
      title="Probestunde anlegen"
      description={`Schritt ${step} von 6`}
      currentStep={step}
      steps={[
        { label: `${getSubjectLabels().singular} & Lehrer` },
        { label: 'Datum' },
        { label: 'Raum' },
        { label: 'Teilnehmer' },
        { label: 'Kommunikation' },
        { label: 'Übersicht' },
      ]}
      onBack={step > 1 ? () => {
        setStep((s) => (Math.max(1, (s as number) - 1) as Step));
      } : undefined}
      canBack={step > 1}
      onNext={step < 6 ? () => {
        const stepError = getNextStepError({
          step,
          canNextFromStep1,
          canNextFromStep2,
          canNextFromStep3,
          canNextFromStep4,
        });
        if (stepError) {
          toast.error(stepError);
          return;
        }
        setStep((s) => (Math.min(6, (s as number) + 1) as Step));
      } : undefined}
      canNext={step < 6 ? canNext : false}
      onPrimary={step === 6 ? submit : undefined}
      primaryLabel="Eintragen"
      canPrimary={step === 6 ? canSubmit : false}
      isLoading={isSubmitting}
      contentClassName={modalContentClassName}
      bodyClassName={step === 1 ? 'px-6 overflow-hidden' : 'px-6'}
    >
      {step === 1 && (
        <StepLessonSchedule
          step1ActiveSection={step1ActiveSection}
          step1UnterrichtCollapsed={step1UnterrichtCollapsed}
          step1TerminplanCollapsed={step1TerminplanCollapsed}
          selectedSubjectName={selectedSubjectName}
          teacherSummaryName={teacherSummaryName}
          selectedCourseId={selectedCourseId}
          selectedCourse={selectedCourse}
          fachOptions={fachOptions}
          fachId={fachId}
          teacherOptions={teacherOptions}
          teacherId={teacherId}
          lockTeacher={lockTeacher}
          teacherAvailability={teacherAvailability}
          selectedSlot={selectedSlot}
          previewEvent={previewEvent}
          timetableDayOfWeek={timetableDayOfWeek}
          timetableReferenceDate={timetableReferenceDate}
          canShowTerminplan={canShowTerminplan}
          missingScheduleSelectionText={missingScheduleSelectionText}
          teacherAvailabilityHint={teacherAvailabilityHint}
          showOutsideAvailabilityWarning={showOutsideAvailabilityWarning}
          onActivateSection={setStep1ActiveSection}
          onSelectFach={(nextFachId) => {
            setFachId(nextFachId);
            setRoomTagsUserModified(false);
            setSelectedRoomTags([]);
            if (!lockTeacher) setTeacherId(undefined);
            setSelectedSlot(null);
            setSelectedCourseId(undefined);
            setSelectedCourse(null);
            setDate('');
            setStartTime('');
          }}
          onSelectTeacher={(nextTeacherId) => {
            setTeacherId(nextTeacherId);
            setSelectedSlot(null);
            setSelectedCourseId(undefined);
            setSelectedCourse(null);
            setDate('');
            setStartTime('');
          }}
          onClearCourse={() => {
            setSelectedCourseId(undefined);
            setSelectedCourse(null);
          }}
          onSelectTimetableSlot={({ dayOfWeek, startTime, endTime }) => {
            setSelectedSlot({ dayOfWeek, startTime, endTime });
            setTimetableDayOfWeek(dayOfWeek);
            setDate('');
            setStartTime(startTime.slice(0, 5));
            setSelectedCourseId(undefined);
            setSelectedCourse(null);
          }}
          onSelectEvent={handleSelectEvent}
          onSelectTimetableDay={setTimetableDayOfWeek}
        />
      )}

      {step === 2 && (
        <StepDateSelection
          selectedSlot={selectedSlot}
          step2HintText={step2HintText}
          datePageModel={datePageModel}
          date={date}
          startTime={startTime}
          teacherDayLoading={teacherDayLoading}
          teacherDayError={teacherDayError}
          hasTeacherCollision={hasTeacherCollision}
          onPrevPage={() => setDatePage((prev) => prev - 1)}
          onNextPage={() => setDatePage((prev) => prev + 1)}
          onSelectDate={(nextDate, nextStartTime) => {
            setDate(nextDate);
            setStartTime(nextStartTime);
          }}
          addMinutes={addMinutes}
          fachAuswahl={
            requireDateSelection
              ? {
                  fachOptions,
                  fachId,
                  onSelectFach: (nextFachId) => {
                    setFachId(nextFachId);
                    setRoomTagsUserModified(false);
                    setSelectedRoomTags([]);
                  },
                }
              : undefined
          }
        />
      )}

      {step === 3 && (
        <StepRoomSelection
          date={date}
          startTime={startTime}
          endTime={endTime}
          roomUnitsLoading={roomUnitsLoading}
          roomUnitsError={roomUnitsError}
          filteredRoomsForFach={filteredRoomsForFach}
          roomUnitsForDate={roomUnitsForDate}
          roomId={roomId}
          teacherRoomsOnSelectedDay={teacherRoomsOnSelectedDay}
          fachId={fachId}
          fachOptions={fachOptions}
          allRoomTags={allRoomTags}
          selectedRoomTags={selectedRoomTags}
          roomSearchQuery={roomSearchQuery}
          onToggleRoomTag={(tag) => {
            setRoomTagsUserModified(true);
            setSelectedRoomTags((prev) => (prev.includes(tag) ? prev.filter((value) => value !== tag) : [...prev, tag]));
          }}
          onChangeRoomSearchQuery={setRoomSearchQuery}
          onSelectRoom={setRoomId}
          onOpenRoomPreview={(room) => {
            setRoomPreview(room);
            setRoomPreviewOpen(true);
          }}
          addMinutes={addMinutes}
          overlaps={overlaps}
        />
      )}

      <Dialog open={roomPreviewOpen} onOpenChange={setRoomPreviewOpen}>
        <DialogContent size="2xl">
          <DialogHeader>
            <DialogTitle>{roomPreview?.name || 'Raum'}</DialogTitle>
          </DialogHeader>
          {roomPreview?.image_url ? (
            <div className="space-y-3">
              <img src={roomPreview.image_url} alt={roomPreview.name} className="w-full rounded-lg border object-contain max-h-[60vh]" />
              {roomPreview.address ? <div className="text-sm text-muted-foreground">{roomPreview.address}</div> : null}
            </div>
          ) : (
            <div className="space-y-2">
              {roomPreview?.address ? <div className="text-sm text-muted-foreground">{roomPreview.address}</div> : null}
              {roomPreview?.tags && roomPreview.tags.length > 0 ? (
                <div className="pt-2">
                  <div className="text-sm font-medium mb-2">Ausstattung</div>
                  <WizardPills>
                    {roomPreview.tags.map((t) => (
                      <WizardPill key={t} active={false}>{t}</WizardPill>
                    ))}
                  </WizardPills>
                </div>
              ) : null}
              <div className="text-sm text-muted-foreground">Keine weiteren Details vorhanden.</div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={coursePickOpen} onOpenChange={(v) => { setCoursePickOpen(v); if (!v) setCoursePick(null); }}>
        <DialogContent size="md">
          <DialogHeader>
            <DialogTitle>Kurs auswählen</DialogTitle>
            <DialogDescription>
              Möchtest du die Probestunde nur zu diesem Zeitpunkt setzen – oder direkt in diesen Kurs legen?
            </DialogDescription>
          </DialogHeader>

          <div className="rounded-md border p-3 text-sm">
            {coursePickLoading ? (
              <div className="text-muted-foreground">Lade Kurs…</div>
            ) : (
              <>
                <div className="font-medium">{coursePick?.course?.name || 'Kurs'}</div>
                <div className="text-xs text-muted-foreground mt-1">
                  {coursePick?.date || '—'} • {coursePick?.startTime || '—'}–{coursePick?.endTime || '—'}
                </div>
              </>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => setCoursePickOpen(false)}>
              Abbrechen
            </Button>
            <Button
              type="button"
              variant="secondary"
              disabled={coursePickLoading || !coursePick}
              onClick={() => {
                if (!coursePick) return;
                setSelectedCourseId(undefined);
                setSelectedCourse(null);
                setDate(coursePick.date);
                setStartTime(coursePick.startTime);
                setSelectedSlot({ dayOfWeek: coursePick.dayOfWeek, startTime: coursePick.startTime + ':00', endTime: addMinutes(coursePick.startTime, 30) });
                setCoursePickOpen(false);
              }}
            >
              Zur gleichen Zeit wie dieser Kurs
            </Button>
            <Button
              type="button"
              disabled={coursePickLoading || !coursePick}
              onClick={() => {
                if (!coursePick) return;
                setSelectedCourseId(coursePick.courseId);
                setSelectedCourse(coursePick.course ?? null);
                const courseRoomId = coursePick.course?.default_raum_id ?? null;
                if (typeof courseRoomId === 'number' && courseRoomId > 0) setRoomId(courseRoomId);
                setDate(coursePick.date);
                setStartTime(coursePick.startTime);
                setSelectedSlot({ dayOfWeek: coursePick.dayOfWeek, startTime: coursePick.startTime + ':00', endTime: addMinutes(coursePick.startTime, 30) });
                setCoursePickOpen(false);
              }}
            >
              In diesen Kurs
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {step === 4 && (
        <StepParticipantSelection
          wert={kundeProfil}
          onChange={setKundeProfil}
          standortId={selectedStandortId ?? null}
          pruefen={teilnehmerGeprueft}
          ausAnfrage={
            trialRequestId
              ? {
                  firstName: trialRequestPerson?.firstName || '',
                  lastName: trialRequestPerson?.lastName || '',
                  email: trialRequestPerson?.email || '',
                  phone: trialRequestPerson?.phone || '',
                }
              : undefined
          }
        />
      )}

      {step === 5 && (
        <StepCommunication
          isActive={open && step === 5}
          selectedStandortId={selectedStandortId ?? undefined}
          composerGroups={composerGroups}
          setSkipStudentEmail={setSkipStudentEmail}
          setSkipTeacherEmail={setSkipTeacherEmail}
          setSkipStudentSms={setSkipStudentSms}
          setSkipTeacherSms={setSkipTeacherSms}
          trialRequestId={trialRequestId}
          createPerson={kundeProfil.personId === null}
          selectedTeilnehmer={selectedTeilnehmer}
          onDrafts={setMailEntwuerfe}
        />
      )}

      {step === 6 && (
        <StepSummary
          fachOptions={fachOptions}
          fachId={fachId}
          teacherDisplayName={teacherDisplayName}
          date={date}
          startTime={startTime}
          endTime={endTime}
          selectedCourseId={selectedCourseId}
          selectedCourse={selectedCourse}
          selectedStandort={selectedStandort}
          selectedStandortId={selectedStandortId ?? undefined}
          roomId={roomId}
          rooms={rooms}
          formatRoomAddress={formatRoomAddress}
          selectedTeilnehmer={selectedTeilnehmer}
          trialRequestPerson={trialRequestPerson}
          skipStudentEmail={skipStudentEmail}
          skipTeacherEmail={skipTeacherEmail}
          skipStudentSms={skipStudentSms}
          skipTeacherSms={skipTeacherSms}
          createHistoryText={createHistoryText}
          autoTaskPreview={historyTaskPreview}
          onChangeHistoryText={(v) => {
            setCreateHistoryTouched(true);
            setCreateHistoryText(v);
          }}
        />
      )}
    </WizardShell>
  );
};

export default TrialWizardModal;

